<template>
  <div
    class="personalization-row row"
    :class="{ 'personalization-row--split-full': variant === 'full' }"
  >
    <div
      v-if="variant === 'full' || variant === 'displayName'"
      class="personalization-col"
      :class="resolvedNameColClass"
    >
      <div
        class="members-settings-card h-100"
        :class="{ 'twitch-theme': isTwitchUser }"
      >
        <div class="members-settings-card-header">
          <h4 class="members-settings-card-title">{{ labels.displayNameTitle }}</h4>
          <p class="members-settings-card-intro">{{ labels.displayNameHint }}</p>
        </div>
        <div class="members-settings-card-body">
          <div class="row align-items-end">
            <div class="col-md-8">
              <div class="form-group mb-0">
                <label for="useNamePersonalization" class="control-label label-uppercase">{{ labels.displayNameLabel }}</label>
                <input
                  id="useNamePersonalization"
                  v-model="useNameDraft"
                  type="text"
                  class="form-control border-input"
                  maxlength="256"
                  autocomplete="name"
                />
              </div>
            </div>
            <div class="col-md-4 mt-3 mt-md-0 d-flex justify-content-md-end">
              <button
                type="button"
                class="btn btn-primary"
                :disabled="isSavingUseName || displayNameSubmitDisabled"
                @click="saveDisplayName"
              >
                {{ isSavingUseName ? labels.saving : labels.savePreferences }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="variant === 'full' || variant === 'customLogo'"
      class="personalization-col"
      :class="resolvedLogoColClass"
    >
      <div
        class="members-settings-card h-100"
        :class="{ 'twitch-theme': isTwitchUser }"
      >
        <div class="members-settings-card-header">
          <h4 class="members-settings-card-title">{{ labels.customLogoTitle }}</h4>
          <p class="members-settings-card-intro">{{ labels.customLogoHint }}</p>
        </div>
        <div class="members-settings-card-body">
          <div class="mb-3 endcard-switch-row">
            <span class="control-label label-uppercase d-block mb-2">{{ labels.activeVideoEndcardLabel }}</span>
            <button
              type="button"
              class="endcard-switch-track"
              :class="{ 'is-on': activeVideoEndcardDraft }"
              :disabled="isSavingLogoCard"
              :aria-pressed="activeVideoEndcardDraft ? 'true' : 'false'"
              :aria-label="labels.activeVideoEndcardSwitchAria"
              @click="toggleActiveVideoEndcardDraft"
            >
              <span class="endcard-switch-thumb" aria-hidden="true"></span>
            </button>
          </div>
          <div class="custom-logo-section">
            <div
              class="custom-logo-preview-stage"
              :class="{ 'custom-logo-preview-stage--endcard': logoPreviewMode === 'endcard' }"
            >
              <div v-if="logoPreviewMode === 'endcard'" class="endcard-preview-frame" :class="{ 'is-disabled': !activeVideoEndcardDraft }">
                <span v-if="!activeVideoEndcardDraft" class="endcard-preview-disabled-chip">
                  {{ tt("common.disabled", "Desactivado") }}
                </span>
                <div class="endcard-preview-logo-wrap">
                  <img
                    :key="customLogoPreviewDisplayKey"
                    :src="customLogoPreviewDisplayUrl"
                    :alt="labels.customLogoPreviewAlt"
                    class="endcard-preview-logo"
                    @error="handleCustomLogoPreviewError"
                  />
                </div>
                <div class="endcard-preview-handle">
                  {{ endcardPreviewHandle }}
                </div>
              </div>
              <div v-else class="custom-logo-preview-frame">
                <img
                  :key="customLogoPreviewDisplayKey"
                  :src="customLogoPreviewDisplayUrl"
                  :alt="labels.customLogoPreviewAlt"
                  class="custom-logo-preview-image"
                  @error="handleCustomLogoPreviewError"
                />
              </div>
            </div>

            <div class="custom-logo-controls">
              <div class="form-group mb-0 custom-logo-field-group">
                <label for="customLogoFilePersonalization" class="control-label label-uppercase">{{ labels.customLogoLabel }}</label>
                <div class="custom-logo-file-button-row">
                  <div class="custom-logo-file-input-wrap">
                    <input
                      id="customLogoFilePersonalization"
                      ref="customLogoFileInput"
                      type="file"
                      class="custom-logo-file-input-native"
                      accept=".png,image/png"
                      tabindex="-1"
                      @change="onCustomLogoSelected"
                    />
                    <button
                      type="button"
                      class="form-control border-input custom-logo-file-input custom-logo-file-pick-btn text-start"
                      @click="onCustomLogoPickClick"
                    >
                      {{ customLogoFileName || labels.customLogoBrowseButton }}
                    </button>
                  </div>
                  <button
                    type="button"
                    class="btn btn-primary custom-logo-save-btn"
                    :disabled="isSavingLogoCard || logoCardSaveDisabled"
                    @click="saveLogoCard"
                  >
                    {{ isSavingLogoCard ? labels.saving : labels.savePreferences }}
                  </button>
                </div>
                <small class="form-text text-muted custom-logo-file-hint">
                  {{ labels.customLogoFileHint }}
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <schedule-subscription-prompt-modal
      v-model="showScheduleSubscriptionPromptModal"
      :has-active-plan="scheduleSubscriptionHasActivePlan"
      @close="closeScheduleSubscriptionPromptModal"
      @subscribe="openSubscriptionFromSchedulePrompt"
    />
  </div>
</template>

<script>
import { mapState } from "pinia";
import { supabase } from "@/supabase";
import { getEffectiveUserId, buildAuthHeaders, isImpersonating } from "@/utils/impersonation";
import { useCurrentUserStore } from "@/stores/currentUser";
import { getApiUrl as resolveApiUrl } from "@/api/client";
import ScheduleSubscriptionPromptModal from "@/components/common/ScheduleSubscriptionPromptModal.vue";

export default {
  components: {
    ScheduleSubscriptionPromptModal,
  },
  props: {
    isTwitchUser: {
      type: Boolean,
      default: false,
    },
    evenColumnSplit: {
      type: Boolean,
      default: false,
    },
    variant: {
      type: String,
      default: "full",
      validator(value) {
        return ["full", "displayName", "customLogo"].includes(value);
      },
    },
    logoPreviewMode: {
      type: String,
      default: "logo",
      validator(value) {
        return ["logo", "endcard"].includes(value);
      },
    },
  },
  data() {
    return {
      useNameDraft: "",
      isSavingUseName: false,
      isSavingLogoCard: false,
      customLogoFile: null,
      customLogoFileName: "",
      customLogoCacheBust: Date.now(),
      customLogoPreviewErrored: false,
      showScheduleSubscriptionPromptModal: false,
      scheduleSubscriptionHasActivePlan: false,
      customLogoObjectPreviewUrl: null,
      customLogoLastSavedUrl: "",
      activeVideoEndcardDraft: false,
    };
  },
  watch: {
    profile: {
      deep: true,
      handler() {
        this.syncUseNameFromProfile();
        this.syncActiveVideoEndcardFromProfile();
      },
    },
  },
  computed: {
    ...mapState(useCurrentUserStore, ["profile"]),
    labels() {
      return {
        saving: this.tt("common.saving", "Guardando..."),
        sessionError: this.tt("userProfile.sessionError", "Error: Sesión no encontrada. Por favor, inicia sesión de nuevo."),
        customLogoTitle: this.tt("userProfile.customLogoTitle", "Logo final de tus clips"),
        customLogoHint: this.tt(
          "userProfile.customLogoHint",
          "Sube un PNG con fondo transparente para la pantalla final. Si no subes uno, se usará el logo de Clipr por defecto. También puedes desactivar por completo la pantalla final con el interruptor de abajo.",
        ),
        customLogoLabel: this.tt("userProfile.customLogoLabel", "Archivo PNG"),
        customLogoBrowseButton: this.tt("userProfile.customLogoBrowseButton", "Seleccionar archivo"),
        customLogoFileHint: this.tt(
          "userProfile.customLogoFileHint",
          "PNG sin fondo · máx. 2 MB · máx. 1024×1024 px. Elige un archivo.",
        ),
        savePreferences: this.tt("userProfile.savePreferences", "Guardar"),
        customLogoSaved: this.tt("userProfile.customLogoSaved", "Logo actualizado correctamente"),
        customLogoSaveError: this.tt("userProfile.customLogoSaveError", "Error al guardar el logo"),
        customLogoInvalidFile: this.tt("userProfile.customLogoInvalidFile", "Solo se permiten archivos PNG (.png)."),
        customLogoFileTooLarge: this.tt(
          "userProfile.customLogoFileTooLarge",
          "El archivo supera el tamaño máximo de 2 MB.",
        ),
        customLogoDimensionsTooLarge: this.tt(
          "userProfile.customLogoDimensionsTooLarge",
          "La imagen supera el tamaño máximo de 1024×1024 px.",
        ),
        customLogoImageReadError: this.tt(
          "userProfile.customLogoImageReadError",
          "No se pudo leer la imagen. Comprueba que sea un PNG válido.",
        ),
        customLogoPreviewAlt: this.tt("userProfile.customLogoPreviewAlt", "Vista previa del logo"),
        activeVideoEndcardLabel: this.tt(
          "userProfile.activeVideoEndcardLabel",
          "Mostrar pantalla final (logo y nombre) en los clips",
        ),
        activeVideoEndcardSaved: this.tt(
          "userProfile.activeVideoEndcardSaved",
          "Preferencia de pantalla final actualizada",
        ),
        activeVideoEndcardSaveError: this.tt(
          "userProfile.activeVideoEndcardSaveError",
          "Error al guardar la pantalla final",
        ),
        activeVideoEndcardSwitchAria: this.tt(
          "userProfile.activeVideoEndcardSwitchAria",
          "Activar o desactivar la pantalla final en los clips",
        ),
        customLogoSectionSavedBoth: this.tt(
          "userProfile.customLogoSectionSavedBoth",
          "Pantalla final y logo actualizados correctamente",
        ),
        displayNameTitle: this.tt("userProfile.displayNameTitle", "Nombre de usuario"),
        displayNameHint: this.tt(
          "userProfile.displayNameHint",
          "Es el nombre que se muestra en la pantalla final de tus clips y en tu perfil.",
        ),
        displayNameLabel: this.tt("userProfile.displayNameLabel", "Nombre visible"),
        displayNameSaved: this.tt("userProfile.displayNameSaved", "Nombre actualizado correctamente"),
        displayNameSaveError: this.tt("userProfile.displayNameSaveError", "Error al guardar el nombre"),
        displayNameRequired: this.tt("userProfile.displayNameRequired", "Introduce un nombre."),
      };
    },
    personalizationNameColClass() {
      const margin = this.evenColumnSplit ? "mb-3 mb-lg-0" : "mb-3 mb-md-0";
      const cols = this.evenColumnSplit
        ? "col-xl-6 col-lg-6 col-md-12"
        : "col-xl-4 col-lg-5 col-md-6";
      return [margin, cols].join(" ");
    },
    personalizationLogoColClass() {
      return this.evenColumnSplit
        ? "col-xl-6 col-lg-6 col-md-12"
        : "col-xl-8 col-lg-7 col-md-6";
    },
    resolvedNameColClass() {
      if (this.variant === "displayName") {
        return "col-12";
      }
      return this.personalizationNameColClass;
    },
    resolvedLogoColClass() {
      if (this.variant === "customLogo") {
        return "col-12";
      }
      return this.personalizationLogoColClass;
    },
    defaultCustomLogoUrl() {
      return "https://arbatech.systemacrm.com/storage/logos/new_clipr_logo_1.png";
    },
    customLogoPreviewBaseUrl() {
      const customLogo = String(
        this.customLogoLastSavedUrl || this.profile?.user_custom_logo || "",
      ).trim();
      if (customLogo && !this.customLogoPreviewErrored) {
        return customLogo;
      }
      return this.defaultCustomLogoUrl;
    },
    customLogoPreviewUrl() {
      const separator = this.customLogoPreviewBaseUrl.includes("?") ? "&" : "?";
      return `${this.customLogoPreviewBaseUrl}${separator}v=${this.customLogoCacheBust}`;
    },
    customLogoPreviewDisplayUrl() {
      if (this.customLogoObjectPreviewUrl) {
        return this.customLogoObjectPreviewUrl;
      }
      return this.customLogoPreviewUrl;
    },
    customLogoPreviewDisplayKey() {
      if (this.customLogoObjectPreviewUrl) {
        return this.customLogoObjectPreviewUrl;
      }
      return this.customLogoPreviewUrl;
    },
    endcardPreviewHandle() {
      const draft = String(this.useNameDraft || "").trim();
      if (draft) {
        return draft;
      }
      const stored = String(this.profile?.use_name || "").trim();
      return stored || "clipr";
    },
    displayNameSubmitDisabled() {
      const draft = String(this.useNameDraft || "").trim();
      const stored = String(this.profile?.use_name || "").trim();
      return !draft || draft === stored;
    },
    activeVideoEndcardSubmitDisabled() {
      const stored = this.profile?.active_video_endcard === true;
      const draft = Boolean(this.activeVideoEndcardDraft);
      return draft === stored;
    },
    logoCardSaveDisabled() {
      return !this.customLogoFile && this.activeVideoEndcardSubmitDisabled;
    },
  },
  async mounted() {
    const currentUserStore = useCurrentUserStore();
    if (!currentUserStore.profile) {
      await currentUserStore.ensureSessionReady();
    }
    this.syncUseNameFromProfile();
    this.syncActiveVideoEndcardFromProfile();
  },
  beforeDestroy() {
    this.revokeCustomLogoObjectPreview();
  },
  methods: {
    tt(key, fallback) {
      return typeof this.$t === "function" ? this.$t(key) : fallback;
    },
    syncUseNameFromProfile() {
      this.useNameDraft = String(useCurrentUserStore().profile?.use_name || "");
    },
    syncActiveVideoEndcardFromProfile() {
      const p = useCurrentUserStore().profile;
      this.activeVideoEndcardDraft = p?.active_video_endcard === true;
    },
    toggleActiveVideoEndcardDraft() {
      if (this.isSavingLogoCard) {
        return;
      }
      this.activeVideoEndcardDraft = !this.activeVideoEndcardDraft;
    },
    revokeCustomLogoObjectPreview() {
      if (this.customLogoObjectPreviewUrl) {
        URL.revokeObjectURL(this.customLogoObjectPreviewUrl);
        this.customLogoObjectPreviewUrl = null;
      }
    },
    async resolveCustomLogoSubscriptionGate(existingSession = null) {
      if (isImpersonating()) {
        this.scheduleSubscriptionHasActivePlan = true;
        return true;
      }
      try {
        const currentUserStore = useCurrentUserStore();
        await currentUserStore.ensureSessionReady(existingSession, { syncCommerce: true, forceCommerce: true });
        const resolvedRole = String(currentUserStore.useRole || "").trim().toLowerCase();
        if (resolvedRole === "admin") {
          this.scheduleSubscriptionHasActivePlan = true;
          return true;
        }
        this.scheduleSubscriptionHasActivePlan = currentUserStore.hasWatermark === false;
        return this.scheduleSubscriptionHasActivePlan;
      } catch (error) {
        console.warn("No se pudo validar la suscripción antes de personalizar el logo:", error);
        this.scheduleSubscriptionHasActivePlan = false;
        return false;
      }
    },
    openScheduleSubscriptionPromptModal() {
      this.showScheduleSubscriptionPromptModal = true;
    },
    closeScheduleSubscriptionPromptModal() {
      this.showScheduleSubscriptionPromptModal = false;
      this.scheduleSubscriptionHasActivePlan = false;
    },
    async openSubscriptionFromSchedulePrompt() {
      this.closeScheduleSubscriptionPromptModal();
      if (!this.$router) return;
      try {
        await this.$router.push({ name: "billing" });
      } catch (error) {
        const message = String(error?.message || "");
        if (!message.includes("NavigationDuplicated")) {
          this.$notify({
            message: `${this.tt("common.error", "Error")}: ${message || this.tt("schedulePost.errorOpenSettings", "No se pudo abrir configuración.")}`,
            type: "danger",
          });
        }
      }
    },
    async onCustomLogoPickClick() {
      const allowed = await this.resolveCustomLogoSubscriptionGate();
      if (!allowed) {
        this.openScheduleSubscriptionPromptModal();
        return;
      }
      const input = this.$refs.customLogoFileInput;
      if (input) {
        input.click();
      }
    },
    async saveDisplayName() {
      if (this.isSavingUseName || this.displayNameSubmitDisabled) {
        return true;
      }
      const trimmed = String(this.useNameDraft || "").trim();
      if (!trimmed) {
        this.$notify({
          message: this.labels.displayNameRequired,
          icon: "ti-alert",
          horizontalAlign: "right",
          verticalAlign: "top",
          type: "danger",
        });
        return false;
      }
      this.isSavingUseName = true;
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user?.id) {
          throw new Error(this.labels.sessionError);
        }
        const userId = getEffectiveUserId(session.user.id);
        if (!userId) {
          throw new Error(this.labels.sessionError);
        }
        const apiUrl = resolveApiUrl();
        const response = await fetch(`${apiUrl}/momo/get-users/display-name`, {
          method: "PATCH",
          headers: buildAuthHeaders(session),
          body: JSON.stringify({
            user_id: userId,
            use_name: trimmed,
          }),
        });
        if (!response.ok) {
          const errData = await response.json().catch(() => null);
          throw new Error(errData?.detail || response.statusText || this.labels.displayNameSaveError);
        }
        await useCurrentUserStore().refresh(session);
        this.syncUseNameFromProfile();
        this.$notify({
          message: this.labels.displayNameSaved,
          icon: "ti-check",
          horizontalAlign: "right",
          verticalAlign: "top",
          type: "success",
        });
        return true;
      } catch (error) {
        console.error("Error saving display name:", error);
        this.$notify({
          message: `${this.labels.displayNameSaveError}: ${error.message || error}`,
          icon: "ti-alert",
          horizontalAlign: "right",
          verticalAlign: "top",
          type: "danger",
        });
        return false;
      } finally {
        this.isSavingUseName = false;
      }
    },
    async saveLogoCard() {
      if (this.isSavingLogoCard || this.logoCardSaveDisabled) {
        return true;
      }
      const hasLogo = Boolean(this.customLogoFile);
      const endcardDirty = !this.activeVideoEndcardSubmitDisabled;
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) {
        this.$notify({
          message: this.labels.sessionError,
          icon: "ti-alert",
          horizontalAlign: "right",
          verticalAlign: "top",
          type: "danger",
        });
        return false;
      }
      if (hasLogo) {
        const canSave = await this.resolveCustomLogoSubscriptionGate(session);
        if (!canSave) {
          this.openScheduleSubscriptionPromptModal();
          return false;
        }
      }
      this.isSavingLogoCard = true;
      const store = useCurrentUserStore();
      const apiUrl = resolveApiUrl();
      const userId = getEffectiveUserId(session.user.id);
      let savedEndcard = false;
      let savedLogo = false;
      try {
        if (!userId) {
          throw new Error(this.labels.sessionError);
        }
        if (endcardDirty) {
          const endNext = Boolean(this.activeVideoEndcardDraft);
          const endResponse = await fetch(`${apiUrl}/momo/get-users/active-video-endcard`, {
            method: "PATCH",
            headers: buildAuthHeaders(session),
            body: JSON.stringify({
              user_id: userId,
              active_video_endcard: endNext,
            }),
          });
          if (!endResponse.ok) {
            const errData = await endResponse.json().catch(() => null);
            throw new Error(errData?.detail || endResponse.statusText || this.labels.activeVideoEndcardSaveError);
          }
          savedEndcard = true;
        }
        if (hasLogo) {
          const formData = new FormData();
          formData.append("file", this.customLogoFile);
          const logoResponse = await fetch(`${apiUrl}/momo/get-users/custom-logo`, {
            method: "POST",
            headers: buildAuthHeaders(session, { includeContentType: false }),
            body: formData,
          });
          if (!logoResponse.ok) {
            const errData = await logoResponse.json().catch(() => null);
            throw new Error(errData?.detail || logoResponse.statusText || this.labels.customLogoSaveError);
          }
          const payload = await logoResponse.json().catch(() => null);
          const uploadedUrl = String(
            typeof payload?.user_custom_logo === "string"
              ? payload.user_custom_logo
              : typeof payload?.userCustomLogo === "string"
                ? payload.userCustomLogo
                : "",
          ).trim();
          if (!uploadedUrl) {
            console.warn("[PersonalizationCard] Respuesta custom-logo sin user_custom_logo:", payload);
          }
          savedLogo = true;
          if (uploadedUrl) {
            this.customLogoLastSavedUrl = uploadedUrl;
            store.$patch((s) => {
              if (s.profile) {
                s.profile.user_custom_logo = uploadedUrl;
              }
            });
          }
          this.customLogoCacheBust = Date.now();
          this.customLogoPreviewErrored = false;
          this.customLogoFile = null;
          this.customLogoFileName = "";
          this.resetCustomLogoFileInput();
        }
        await store.refresh(session);
        this.syncActiveVideoEndcardFromProfile();
        if (savedEndcard && savedLogo) {
          this.$notify({
            message: this.labels.customLogoSectionSavedBoth,
            icon: "ti-check",
            horizontalAlign: "right",
            verticalAlign: "top",
            type: "success",
          });
        } else if (savedLogo) {
          this.$notify({
            message: this.labels.customLogoSaved,
            icon: "ti-check",
            horizontalAlign: "right",
            verticalAlign: "top",
            type: "success",
          });
        } else if (savedEndcard) {
          this.$notify({
            message: this.labels.activeVideoEndcardSaved,
            icon: "ti-check",
            horizontalAlign: "right",
            verticalAlign: "top",
            type: "success",
          });
        }
        return true;
      } catch (error) {
        console.error("Error saving logo card:", error);
        try {
          await store.refresh(session);
        } catch (_) {
          /* ignore */
        }
        this.syncActiveVideoEndcardFromProfile();
        const msg = String(error?.message || error || "");
        let alertMessage;
        if (endcardDirty && !savedEndcard) {
          alertMessage = `${this.labels.activeVideoEndcardSaveError}: ${msg}`;
        } else if (hasLogo && !savedLogo) {
          alertMessage = `${this.labels.customLogoSaveError}: ${msg}`;
        } else {
          alertMessage = msg;
        }
        this.$notify({
          message: alertMessage,
          icon: "ti-alert",
          horizontalAlign: "right",
          verticalAlign: "top",
          type: "danger",
        });
        return false;
      } finally {
        this.isSavingLogoCard = false;
      }
    },
    async savePendingPersonalizationChanges() {
      const shouldSaveDisplayName = !this.displayNameSubmitDisabled;
      const shouldSaveLogoCard = !this.logoCardSaveDisabled;
      const pendingActiveVideoEndcard = Boolean(this.activeVideoEndcardDraft);

      const displayNameSaved = shouldSaveDisplayName ? await this.saveDisplayName() : true;
      if (!displayNameSaved) return false;
      if (!shouldSaveLogoCard) return true;

      this.activeVideoEndcardDraft = pendingActiveVideoEndcard;
      return this.saveLogoCard();
    },
    async onCustomLogoSelected(event) {
      const file = event?.target?.files?.[0] || null;
      this.customLogoPreviewErrored = false;
      if (!file) {
        this.customLogoFile = null;
        this.customLogoFileName = "";
        this.revokeCustomLogoObjectPreview();
        return;
      }
      const fileName = String(file.name || "");
      const contentType = String(file.type || "").toLowerCase();
      const hasPngExtension = fileName.toLowerCase().endsWith(".png");
      const hasPngContentType = contentType === "image/png";
      if (!hasPngExtension || !hasPngContentType) {
        this.rejectCustomLogoSelection(this.labels.customLogoInvalidFile);
        return;
      }
      const maxBytes = 2 * 1024 * 1024;
      if (file.size > maxBytes) {
        this.rejectCustomLogoSelection(this.labels.customLogoFileTooLarge);
        return;
      }
      const maxPx = 1024;
      let dimensions;
      try {
        dimensions = await this.readLogoImageDimensions(file);
      } catch {
        this.rejectCustomLogoSelection(this.labels.customLogoImageReadError);
        return;
      }
      if (dimensions.width > maxPx || dimensions.height > maxPx) {
        this.rejectCustomLogoSelection(this.labels.customLogoDimensionsTooLarge);
        return;
      }
      const nextBlob = URL.createObjectURL(file);
      if (this.customLogoObjectPreviewUrl) {
        URL.revokeObjectURL(this.customLogoObjectPreviewUrl);
      }
      this.customLogoObjectPreviewUrl = nextBlob;
      this.customLogoLastSavedUrl = "";
      this.customLogoFile = file;
      this.customLogoFileName = fileName;
    },
    rejectCustomLogoSelection(message) {
      this.customLogoFile = null;
      this.customLogoFileName = "";
      this.revokeCustomLogoObjectPreview();
      this.customLogoLastSavedUrl = "";
      this.resetCustomLogoFileInput();
      this.$notify({
        message,
        icon: "ti-alert",
        horizontalAlign: "right",
        verticalAlign: "top",
        type: "danger",
      });
    },
    readLogoImageDimensions(file) {
      return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
          URL.revokeObjectURL(url);
          resolve({ width: img.naturalWidth, height: img.naturalHeight });
        };
        img.onerror = () => {
          URL.revokeObjectURL(url);
          reject(new Error("image load failed"));
        };
        img.src = url;
      });
    },
    handleCustomLogoPreviewError() {
      if (this.customLogoObjectPreviewUrl) {
        return;
      }
      if (this.customLogoPreviewBaseUrl === this.defaultCustomLogoUrl) {
        return;
      }
      this.customLogoPreviewErrored = true;
      this.customLogoCacheBust = Date.now();
    },
    resetCustomLogoFileInput() {
      const input = this.$refs.customLogoFileInput;
      if (input && typeof input.value === "string") {
        input.value = "";
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/sass/paper/variables";

.members-settings-card {
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  color: #212529;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08), 0 0 20px var(--clipr-citrus-glow);
  overflow: hidden;
}

.members-settings-card-header {
  padding: 20px 20px 0;
  border-bottom: 0;
}

.members-settings-card-title {
  margin: 0;
  font-weight: 600;
  font-size: 1.1rem;
  color: #212529;
}

.members-settings-card-intro {
  margin: 0.5rem 0 0;
  font-size: 0.9rem;
  color: #6c757d;
  line-height: 1.45;
}

.members-settings-card-body {
  padding: 15px 20px 20px;
}

.members-settings-card label,
.members-settings-card .form-text,
.members-settings-card .text-muted {
  color: #6c757d;
}

.members-settings-card .form-control,
.members-settings-card .border-input {
  background: #ffffff;
  border-color: #dee2e6;
  color: #212529;
}

.border-input {
  border: 1px solid $table-line-color;
}

.label-uppercase {
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.02em;
}

.endcard-switch-row .endcard-switch-track {
  position: relative;
  display: block;
  width: 52px;
  height: 30px;
  padding: 0;
  margin: 0;
  border: none;
  border-radius: 15px;
  cursor: pointer;
  background: #ced4da;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.12);
  transition: background 0.2s ease;
  flex-shrink: 0;
}

.endcard-switch-row .endcard-switch-track.is-on {
  background: $primary-color;
}

.endcard-switch-row .endcard-switch-track:focus {
  outline: none;
  box-shadow:
    inset 0 1px 2px rgba(0, 0, 0, 0.12),
    0 0 0 3px rgba($primary-color, 0.35);
}

.endcard-switch-row .endcard-switch-track:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.endcard-switch-row .endcard-switch-thumb {
  position: absolute;
  top: 4px;
  left: 4px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #ffffff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
  transition: transform 0.2s ease;
  pointer-events: none;
}

.endcard-switch-row .endcard-switch-track.is-on .endcard-switch-thumb {
  transform: translateX(22px);
}

.custom-logo-section {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 1.35rem;
}

.custom-logo-preview-stage {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
}

.custom-logo-preview-stage--endcard {
  min-height: 320px;
}

.custom-logo-preview-frame {
  box-sizing: border-box;
  width: min(160px, 52vw);
  max-width: 100%;
  aspect-ratio: 1;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem;
  border-radius: 14px;
  background: linear-gradient(160deg, rgba(255, 255, 255, 0.55), rgba(0, 0, 0, 0.03));
  border: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.9),
    0 6px 20px rgba(0, 0, 0, 0.07);
}

.custom-logo-preview-image {
  max-width: 86%;
  max-height: 86%;
  width: auto;
  height: auto;
  object-fit: contain;
  object-position: center;
  display: block;
}

.endcard-preview-frame {
  position: relative;
  box-sizing: border-box;
  width: min(220px, 62vw);
  max-width: 100%;
  aspect-ratio: 9 / 16;
  border-radius: 18px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: #000000;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.08),
    0 12px 28px rgba(0, 0, 0, 0.35);
  padding: 18px 14px 20px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  transition: opacity 0.2s ease;
}

.endcard-preview-frame.is-disabled {
  opacity: 0.65;
}

.endcard-preview-logo-wrap {
  width: 62%;
  max-width: 120px;
  aspect-ratio: 1;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(24, 24, 27, 0.92);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.6rem;
}

.endcard-preview-logo {
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
  display: block;
}

.endcard-preview-handle {
  max-width: 90%;
  font-size: 1rem;
  line-height: 1.15;
  font-weight: 700;
  color: #ffffff;
  text-shadow: 0 3px 12px rgba(0, 0, 0, 0.55);
  word-break: break-word;
  text-align: center;
}

.endcard-preview-disabled-chip {
  position: absolute;
  top: 12px;
  right: 12px;
  font-size: 10px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  font-weight: 700;
  color: #ffffff;
  background: rgba(220, 38, 38, 0.8);
  border: 1px solid rgba(254, 202, 202, 0.8);
  border-radius: 999px;
  padding: 3px 8px;
}

.custom-logo-controls {
  width: 100%;
}

.custom-logo-field-group {
  margin-bottom: 0;
}

.custom-logo-file-button-row {
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  gap: 0.65rem;
  flex-wrap: nowrap;
}

.custom-logo-file-input-wrap {
  flex: 1 1 0;
  min-width: 0;
  position: relative;
}

.custom-logo-file-input {
  width: 100%;
  padding: 0.45rem 0.65rem;
  font-size: 0.875rem;
  line-height: 1.35;
}

.custom-logo-file-input-native {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.custom-logo-file-pick-btn {
  cursor: pointer;
}


.custom-logo-file-hint {
  display: block;
  margin-top: 0.45rem;
  text-align: left;
}

.custom-logo-save-btn {
  flex-shrink: 0;
  align-self: flex-end;
  white-space: nowrap;
}

.members-settings-card .form-group {
  margin-bottom: 1rem;
}

.members-settings-card .btn {
  min-height: 40px;
  padding: 0.55rem 1rem;
}

@media (max-width: 991px) {
  .members-settings-card-header {
    padding: 1rem 1rem 0;
  }

  .members-settings-card-body {
    padding: 0.9rem 1rem 1rem;
  }

}

@media (max-width: 575px) {
  .custom-logo-preview-stage--endcard {
    min-height: 280px;
  }

  .endcard-preview-frame {
    width: min(190px, 78vw);
  }

  .members-settings-card-header {
    padding-left: 1rem;
    padding-right: 1rem;
  }

  .members-settings-card-body {
    padding-left: 1rem;
    padding-right: 1rem;
  }

  .members-settings-card .row {
    margin-left: 0;
    margin-right: 0;
  }

  .members-settings-card .row > [class*="col-"] {
    padding-left: 0;
    padding-right: 0;
  }

  .members-settings-card .row.align-items-end {
    row-gap: 0.85rem;
  }

  .members-settings-card .col-md-4.mt-3.mt-md-0.d-flex.justify-content-md-end {
    justify-content: stretch !important;
  }

  .members-settings-card .btn {
    width: 100%;
  }

  .custom-logo-file-button-row .custom-logo-save-btn {
    width: auto;
  }
}

.members-settings-card.twitch-theme {
  background: var(--clipr-purple-overlay-medium);
  border: 1px solid var(--clipr-border-color);
  color: var(--clipr-text-primary);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
}

.members-settings-card.twitch-theme .members-settings-card-title {
  color: var(--clipr-text-primary);
}

.members-settings-card.twitch-theme .members-settings-card-intro {
  color: var(--clipr-text-secondary);
}

.members-settings-card.twitch-theme label,
.members-settings-card.twitch-theme .form-text,
.members-settings-card.twitch-theme .text-muted {
  color: var(--clipr-text-secondary);
}

.members-settings-card.twitch-theme .form-control,
.members-settings-card.twitch-theme .border-input {
  background: var(--clipr-bg-elevated);
  border-color: var(--clipr-border-color);
  color: var(--clipr-text-primary);
}

.members-settings-card.twitch-theme .btn-primary {
  background: var(--clipr-purple-gradient);
  border: none;
  box-shadow: 0 4px 14px var(--clipr-purple-glow);
  border-radius: 8px;
}

.members-settings-card.twitch-theme .btn-primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #a855f7 0%, #d946ef 100%);
  box-shadow: var(--clipr-shadow-button-hover);
}

.members-settings-card.twitch-theme .custom-logo-preview-frame {
  background: linear-gradient(
    155deg,
    rgba(255, 255, 255, 0.09),
    rgba(0, 0, 0, 0.22)
  );
  border-color: rgba(255, 255, 255, 0.12);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.12),
    0 8px 26px rgba(0, 0, 0, 0.45);
}

.members-settings-card.twitch-theme .endcard-preview-frame {
  border-color: rgba(216, 180, 254, 0.32);
  background: #000000;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.1),
    0 12px 28px rgba(0, 0, 0, 0.45);
}

.members-settings-card.twitch-theme .endcard-preview-logo-wrap {
  border-color: rgba(216, 180, 254, 0.44);
  background: rgba(31, 41, 55, 0.9);
}

.members-settings-card.twitch-theme .endcard-switch-track {
  background: rgba(255, 255, 255, 0.14);
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.35);
}

.members-settings-card.twitch-theme .endcard-switch-track.is-on {
  background: var(--clipr-purple, #a855f7);
}

.members-settings-card.twitch-theme .endcard-switch-track:focus {
  box-shadow:
    inset 0 1px 2px rgba(0, 0, 0, 0.35),
    0 0 0 3px rgba(168, 85, 247, 0.3);
}

.members-settings-card.twitch-theme .custom-logo-file-pick-btn {
  transition: border-color 0.15s ease, background 0.15s ease;
}

.members-settings-card.twitch-theme .custom-logo-file-pick-btn:hover {
  border-color: rgba(168, 85, 247, 0.45);
  background: rgba(168, 85, 247, 0.08);
}

.members-settings-card.twitch-theme .custom-logo-file-hint {
  font-size: 0.72rem;
  color: var(--clipr-text-tertiary, #a8a8b4);
}

.members-settings-card.twitch-theme .endcard-preview-frame {
  border-radius: 16px;
}

.personalization-row--split-full {
  align-items: stretch;
}
</style>
